/* eslint-disable react/jsx-props-no-spreading */
import {
    useReducer,
    useEffect,
    useCallback,
    useMemo, useState,
} from 'react'
import noop from 'lodash/noop'
import createReducer from 'app/utils/createReducer'

import {
    DelayedLoadOptions,
} from 'app/hooks/useDelayedLoadOptions'

import {
    ActionLinkFunction,
} from 'app/types/form.types'
import {
    NotificationIconProps,
} from '../NotificationIcon/notificationIcon.types'

import Typeahead, {
    InputVariant,
} from './Typeahead'

export type Props = {
    name?: string,
    title?: string,
    loadOptions: (options: DelayedLoadOptions) => Promise<any>,
    labelData?: Record<string, any>,
    value?: string | number,
    valueField?: string,
    labelField?: string,
    onChange: (newVal: string | number) => void,
    classNames?: {
        options?: string,
        label?: string,
        labelContainer?: string,
    },
    className?: string,
    isRequired?: boolean,
    multiline?: boolean,
    disabled?: boolean,
    variant?: InputVariant,
    validated?: boolean,
    notificationIconProps: NotificationIconProps,
    errors?: string[],
    labelTooltip?: JSX.Element,
    placeholder?: string,
    noneOption?: boolean,
    minRows?: number,
    maxRows?: number,
    actionLinkFunction?: ActionLinkFunction
}
export const defaultProps = {
    valueField: 'id',
    onChange: noop,
    value: '',
    labelField: '',
    labelData: undefined,
    name: '',
    title: '',
    classNames: {},
    className: '',
    isRequired: false,
    multiline: false,
    disabled: false,
    variant: 'outlined',
    errors: undefined,
    validated: undefined,
    notificationIconProps: undefined,
    labelTooltip: undefined,
    placeholder: undefined,
    noneOption: true,
    minRows: undefined,
    maxRows: undefined,
    actionLinkFunction: undefined,
}

const OPEN_OPTIONS = 'OPEN_OPTIONS'
const CLOSE_OPTIONS = 'CLOSE_OPTIONS'
const SET_SEARCH_PHRASE = 'SET_SEARCH_PHRASE'

export const getLabelValue = (option, labelField: string) => {
    if (!option) {
        return null
    }
    if (Array.isArray(labelField)) {
        return labelField.map((item) => {
            return option[item]
        }).join(' | ')
    }
    return option[labelField]
}

type SearchState = {
    optionsVisible: boolean;
    searchPhrase: string
}

const reducer = createReducer({
    [SET_SEARCH_PHRASE]: (state: SearchState, {
        searchPhrase,
    }) => {
        return {
            ...state,
            searchPhrase,
        }
    },
    [OPEN_OPTIONS]: () => {
        return {
            optionsVisible: true,
            searchPhrase: '',
        }
    },
    [CLOSE_OPTIONS]: () => {
        return {
            optionsVisible: false,
            searchPhrase: '',
        }
    },
})

const TypeaheadObjectContainer = (props: Props) => {
    const {
        loadOptions,
        labelData,
        labelField,
        value,
    } = props

    const [
        {
            optionsVisible,
            searchPhrase,
        },
        dispatch,
    ]: [
        SearchState,
        (args: { type: string, searchPhrase?: string }) => void,
    ] = useReducer(reducer, {
        optionsVisible: false,
        searchPhrase: '',
    })

    const [
        options,
        setOptions,
    ] = useState()

    const label = useMemo(() => {
        return value ? {
            value: getLabelValue(labelData || value, labelField),
            iconPath: labelData?.iconPath,
        } : undefined
    }, [
        labelData,
        labelField,
        value,
    ])

    const openOptions = useCallback(() => {
        dispatch({
            type: OPEN_OPTIONS,
        })
    }, [dispatch])

    const closeOptions = useCallback(() => {
        dispatch({
            type: CLOSE_OPTIONS,
        })
    }, [dispatch])

    const setSearchPhrase = useCallback((newSearchPhrase: string) => {
        dispatch({
            type: SET_SEARCH_PHRASE,
            searchPhrase: newSearchPhrase,
        })
    }, [dispatch])

    const dataNew = useCallback((phrase: string) => {
        loadOptions({
            phrase,
        })
            .then((resp) => {
                setOptions(resp)
            })
    }, [loadOptions])

    useEffect(() => {
        if (searchPhrase) {
            return dataNew(searchPhrase)
        }

        return dataNew('')
    }, [
        dataNew,
        searchPhrase,
    ])

    return (
        <Typeahead
            {...props}
            labelData={label}
            setSearchPhrase={setSearchPhrase}
            searchPhrase={searchPhrase}
            options={options}
            closeOptions={closeOptions}
            optionsVisible={optionsVisible}
            openOptions={openOptions}
            getLabelValue={getLabelValue}
            labelField={labelField}
        />
    )
}

TypeaheadObjectContainer.defaultProps = defaultProps

export default TypeaheadObjectContainer
